import { useCallback, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { ChevronDown, ChevronRight, Link2, RefreshCw, RotateCcw, ShieldOff, X } from 'lucide-react'
import { Modal, Select, useToast } from '../../../admin/primitives.js'
import { Checkbox, Field, TextInput } from '../../../admin/form.js'
import { Combobox, type ComboItem } from '../../../admin/pickers.js'
import { useConfirm } from '../../../shell/confirm.js'
import { Link } from '../../../router.js'
import { fmtDateHeure, fmtHeure, lireSupervision, type Lecture } from '../../api.js'
import { BandeauLecture, fmtAge, fmtNombre } from '../commun.js'
import { ecrireSonde } from './ecrire.js'
import { LigneRetour, Vide, libelleSalle, optionsSalles, type Retour } from './elements.js'
import {
  FAMILLE_TYPE, LIBELLE_CRITICITE, LIBELLE_FAMILLE, LOT_MAX, MOTS_TYPE, TYPES_POSTE, acteur, correspond, decisionsValidation, estListeDecouvertes, estResultatDecisions, grouper, libelleSchema,
  libelleSource, libelleType, ligneInitiale, peutValider, preuvesLisibles, problemeLigne, raisonViePrivee, rattachementPossible,
  uidDepuisNom, uidsUniques, urgenceParDefaut, vlanCompteSeulement,
  type Decouverte, type LigneValidation, type ListeDecouvertes, type SalleRegistre, type StatutDecouverte,
} from './modele.js'

/**
 * ONGLET « À VALIDER » (21/10/2026) — la file des découvertes de Sonde.
 *
 * Tant qu'une découverte n'est pas validée, l'appareil n'existe pas au
 * registre : il n'apparaît dans aucune salle et personne n'est prévenu s'il
 * tombe. C'est l'écran qui fait entrer les TPE, caméras, détecteurs de fumée,
 * compteurs, NVR et lecteurs Access dans la supervision.
 *
 * Gestes (droit sonde.registry.manage) : valider en lot ou un par un (nom,
 * identifiant, salle et type modifiables), rattacher à un poste existant
 * (« exemplaire remplacé » → `replace: true`), ignorer, remettre dans la file.
 * VLAN 30/40 : seulement comptés (vie privée) — seule « Ignorer » existe.
 */

const STATUTS: { value: StatutDecouverte; label: string }[] = [
  { value: 'pending', label: 'À valider' },
  { value: 'accepted', label: 'Validées' },
  { value: 'ignored', label: 'Ignorées' },
]

const GESTES = [
  { value: '', label: 'Tous les gestes proposés' },
  { value: 'accept', label: 'Prêtes à valider' },
  { value: 'attach', label: 'À rattacher à un poste' },
  { value: 'review', label: 'À examiner' },
]

const LIBELLE_GESTE: Record<string, string> = { accept: 'Prête à valider', attach: 'À rattacher', review: 'À examiner' }

export function OngletAValider(props: { peutGerer: boolean; salles: SalleRegistre[] | null; onCompte: () => void }) {
  const { peutGerer, salles } = props
  const toast = useToast()
  const confirm = useConfirm()
  const [statut, setStatut] = useState<StatutDecouverte>('pending')
  const [lecture, setLecture] = useState<Lecture<ListeDecouvertes> | null>(null)
  const [items, setItems] = useState<Decouverte[]>([])
  const [curseur, setCurseur] = useState<string | null>(null)
  const [luA, setLuA] = useState<string | null>(null)
  const [chargement, setChargement] = useState(false)
  const [par, setPar] = useState<'type' | 'source'>('type')
  const [recherche, setRecherche] = useState('')
  const [geste, setGeste] = useState('')
  const [selection, setSelection] = useState<Set<string>>(new Set())
  const [ouverts, setOuverts] = useState<Set<string>>(new Set())
  const [fermes, setFermes] = useState<Set<string>>(new Set())
  const [validation, setValidation] = useState<LigneValidation[] | null>(null)
  const [enCours, setEnCours] = useState<string | null>(null)
  const [retour, setRetour] = useState<Retour>(null)

  const charger = useCallback(async (suite: string | null = null) => {
    setChargement(true)
    const q = `/v/discovery?status=${statut}&limit=500${suite ? `&cursor=${encodeURIComponent(suite)}` : ''}`
    const l = await lireSupervision(q, estListeDecouvertes)
    setChargement(false)
    setLecture(l)
    if (l.etat !== 'ok') return
    setLuA(new Date().toISOString())
    setCurseur(l.data.next_cursor)
    setItems((avant) => {
      if (!suite) return l.data.items
      const vus = new Set(avant.map((d) => d.id))
      return [...avant, ...l.data.items.filter((d) => !vus.has(d.id))]
    })
  }, [statut])

  useEffect(() => { setSelection(new Set()); void charger() }, [charger])

  const relire = () => { void charger(); props.onCompte() }

  const visibles = useMemo(
    () => items.filter((d) => correspond(d, recherche) && (!geste || d.proposed_action === geste)),
    [items, recherche, geste],
  )
  const groupes = useMemo(() => grouper(visibles, par), [visibles, par])
  const selectionnes = useMemo(() => items.filter((d) => selection.has(d.id)), [items, selection])
  const counts = lecture?.etat === 'ok' ? lecture.data.counts : null
  const comptesSeulement = useMemo(() => items.filter((d) => vlanCompteSeulement(d.vlan)).length, [items])

  const basculer = (id: string, on: boolean) => setSelection((s) => {
    const n = new Set(s)
    if (on) { if (n.size < LOT_MAX) n.add(id) } else n.delete(id)
    return n
  })
  const basculerGroupe = (ids: string[], on: boolean) => setSelection((s) => {
    const n = new Set(s)
    for (const id of ids) {
      if (!on) n.delete(id)
      else if (n.size < LOT_MAX) n.add(id)
    }
    return n
  })

  /** Retire de la liste ce qui a quitté le statut affiché. */
  const retirer = (ids: string[]) => {
    const r = new Set(ids)
    setItems((l) => l.filter((d) => !r.has(d.id)))
    setSelection((s) => new Set([...s].filter((id) => !r.has(id))))
  }

  const ignorer = async (ds: Decouverte[]) => {
    if (ds.length === 0) return
    if (ds.length > 1) {
      const ok = await confirm({
        title: `Ignorer ${ds.length} découvertes ?`,
        message: 'Elles ne seront plus proposées et n’entreront pas au registre : aucune supervision pour elles. Elles restent comptées, et chacune peut être remise dans la file plus tard.',
        confirmLabel: 'Ignorer', variant: 'sensitive',
      })
      if (!ok) return
    }
    setEnCours(ds.length === 1 ? ds[0]!.id : 'lot')
    const e = ds.length === 1
      ? await ecrireSonde('POST', `discovery/${encodeURIComponent(ds[0]!.id)}/ignore`, {})
      : await ecrireSonde('POST', 'discovery', { decisions: ds.map((d) => ({ id: d.id, action: 'ignore' })) })
    setEnCours(null)
    if (!e.ok) { setRetour({ ton: 'ko', texte: e.message }); return }
    if (ds.length > 1 && estResultatDecisions(e.data)) {
      retirer(e.data.results.filter((r) => r.ok).map((r) => r.id))
      setRetour(e.data.failed > 0
        ? { ton: 'ko', texte: `${e.data.applied} ignorée${e.data.applied > 1 ? 's' : ''}, ${e.data.failed} refusée${e.data.failed > 1 ? 's' : ''} : ${e.data.results.find((r) => !r.ok)?.message_fr ?? ''}` }
        : { ton: 'ok', texte: `${e.data.applied} découverte${e.data.applied > 1 ? 's' : ''} ignorée${e.data.applied > 1 ? 's' : ''} : elles ne seront plus proposées.` })
    } else {
      retirer(ds.map((d) => d.id))
      toast('ok', 'Ignorée : elle ne sera plus proposée (elle reste comptée).')
    }
    props.onCompte()
  }

  const remettre = async (d: Decouverte) => {
    setEnCours(d.id)
    const e = await ecrireSonde('POST', `discovery/${encodeURIComponent(d.id)}/reset`, {})
    setEnCours(null)
    if (!e.ok) { setRetour({ ton: 'ko', texte: e.message }); return }
    retirer([d.id])
    toast('ok', 'Remise dans la file « À valider ».')
    props.onCompte()
  }

  const rattacher = async (d: Decouverte) => {
    const h = d.attach_hint
    if (!h) return
    const ok = await confirm({
      title: h.replacement ? `Exemplaire remplacé sur « ${h.display_name} » ?` : `Rattacher à « ${h.display_name} » ?`,
      message: h.replacement
        ? 'Le nouvel identifiant est rattaché au poste et l’ancien est clos : l’historique suit le poste, la salle et les alertes ne changent pas.'
        : 'L’identifiant est rattaché à ce poste : Sonde le suivra sous ce nom, dans sa salle.',
      details: [
        { label: 'Poste', value: h.uid },
        { label: 'Identifiant', value: `${libelleSchema(d.scheme)} ${d.value}` },
        ...(h.replacement && h.current_values.length ? [{ label: 'Clos', value: h.current_values.join(', ') }] : []),
      ],
      confirmLabel: h.replacement ? 'Rattacher en remplaçant' : 'Rattacher',
      variant: h.replacement ? 'sensitive' : 'standard',
    })
    if (!ok) return
    setEnCours(d.id)
    const e = await ecrireSonde('POST', `discovery/${encodeURIComponent(d.id)}/attach`, { uid: h.uid, ...(h.replacement ? { replace: true } : {}) })
    setEnCours(null)
    if (!e.ok) { setRetour({ ton: 'ko', texte: e.message }); return }
    retirer([d.id])
    toast('ok', h.replacement ? `Rattachée à « ${h.uid} » ; l’ancien exemplaire est clos.` : `Rattachée à « ${h.uid} ».`)
    props.onCompte()
  }

  const ouvrirValidation = (ds: Decouverte[]) => {
    const valides = ds.filter(peutValider).slice(0, LOT_MAX)
    if (valides.length === 0) return
    const lignes = valides.map(ligneInitiale)
    const uids = uidsUniques(lignes.map((l) => l.uid))
    setValidation(lignes.map((l, i) => (l.geste === 'accept' ? { ...l, uid: uids[i]! } : l)))
  }

  const selectionValidable = selectionnes.filter(peutValider)

  return (
    <section className="sv-rgs-onglet" aria-label="Découvertes à valider">
      <div className="sv-rgs-outils">
        <Select compact ariaLabel="Statut des découvertes" value={statut} options={STATUTS} onChange={(v) => setStatut(v as StatutDecouverte)} />
        <Select compact ariaLabel="Grouper par" value={par} onChange={(v) => setPar(v as 'type' | 'source')}
          options={[{ value: 'type', label: 'Grouper par type' }, { value: 'source', label: 'Grouper par source' }]} />
        {statut === 'pending' ? <Select compact ariaLabel="Geste proposé" value={geste} options={GESTES} onChange={setGeste} /> : null}
        <div className="sv-rgs-outils__recherche">
          <TextInput value={recherche} onChange={setRecherche} placeholder="Nom, salle, fabricant, valeur…" ariaLabel="Rechercher une découverte" maxLength={100} />
        </div>
        <button type="button" className="lc-btn lc-btn--ghost" onClick={relire} disabled={chargement}>
          <RefreshCw size={14} aria-hidden="true" /> {chargement ? 'Lecture…' : 'Relire'}
        </button>
      </div>

      <p className="sv-rgs-source">
        Source : Sonde, file « À valider »{luA ? ` · lue à ${fmtHeure(luA)}` : ''}
        {counts ? ` · ${fmtNombre(counts.pending)} en attente, ${fmtNombre(counts.accepted)} validées, ${fmtNombre(counts.ignored)} ignorées` : ''}
      </p>

      <BandeauLecture lecture={lecture} depuis={luA} />
      <LigneRetour retour={retour} />

      {statut === 'pending' && !peutGerer && items.length > 0 ? (
        <p className="sv-rgs-lecture-seule" role="note">Lecture seule : valider ou ignorer demande le droit « Gérer le registre de la supervision ».</p>
      ) : null}

      {statut === 'pending' && comptesSeulement > 0 ? (
        <p className="sv-rgs-note" role="note">
          <ShieldOff size={15} aria-hidden="true" />{' '}
          <span>{comptesSeulement} découverte{comptesSeulement > 1 ? 's' : ''} vue{comptesSeulement > 1 ? 's' : ''} sur les VLAN 30 (personnel) ou 40 (invités) : ces appareils sont seulement comptés, jamais suivis un par un (vie privée). Seule « Ignorer » est possible.</span>
        </p>
      ) : null}

      {lecture?.etat === 'ok' && items.length === 0 ? (
        statut === 'pending' ? (
          <Vide titre="Rien à valider pour l’instant.">
            Chaque appareil vu par Sonde et rattaché à aucun poste arrive ici. Quand Sonde en découvrira de nouveaux (caméras, TPE,
            capteurs…), ils apparaîtront dans cette file. Dernière lecture : {fmtHeure(luA)}.
          </Vide>
        ) : <Vide titre={statut === 'accepted' ? 'Aucune découverte validée.' : 'Aucune découverte ignorée.'} />
      ) : null}

      {items.length > 0 && visibles.length === 0 ? <Vide titre="Aucune découverte ne correspond à ces filtres." /> : null}

      <div className="sv-rgs-groupes">
        {groupes.map((g) => {
          const ferme = fermes.has(g.cle)
          const cochables = g.items.filter(peutValider).map((d) => d.id)
          const tousCoches = cochables.length > 0 && cochables.every((id) => selection.has(id))
          return (
            <section key={g.cle || '_'} className="lc-panel sv-rgs-groupe" aria-label={g.titre}>
              <header className="lc-panel__head sv-rgs-groupe__tete">
                {peutGerer && statut === 'pending' && cochables.length > 0 ? (
                  <Checkbox checked={tousCoches} onChange={(v) => basculerGroupe(cochables, v)} ariaLabel={`Tout sélectionner : ${g.titre}`} />
                ) : null}
                <button type="button" className="sv-rgs-groupe__titre" aria-expanded={!ferme}
                  onClick={() => setFermes((s) => { const n = new Set(s); if (n.has(g.cle)) n.delete(g.cle); else n.add(g.cle); return n })}>
                  {ferme ? <ChevronRight size={16} aria-hidden="true" /> : <ChevronDown size={16} aria-hidden="true" />}
                  <span>{g.titre}</span>
                  <span className="sv-rgs-compte">{g.items.length}</span>
                </button>
                {peutGerer && statut === 'pending' && cochables.length > 0 ? (
                  <button type="button" className="lc-btn lc-btn--sm sv-rgs-groupe__action" onClick={() => ouvrirValidation(g.items)}>
                    Valider le groupe
                  </button>
                ) : null}
              </header>
              {!ferme ? (
                <ul className="sv-rgs-liste">
                  {g.items.map((d) => (
                    <LigneDecouverte key={d.id} d={d} par={par} salles={salles} peutGerer={peutGerer && d.status === 'pending'}
                      coche={selection.has(d.id)} ouvert={ouverts.has(d.id)} occupe={enCours === d.id || enCours === 'lot'}
                      onCocher={(v) => basculer(d.id, v)}
                      onOuvrir={() => setOuverts((s) => { const n = new Set(s); if (n.has(d.id)) n.delete(d.id); else n.add(d.id); return n })}
                      onValider={() => ouvrirValidation([d])} onRattacher={() => void rattacher(d)} onIgnorer={() => void ignorer([d])}
                      onRemettre={peutGerer && d.status === 'ignored' ? () => void remettre(d) : null} />
                  ))}
                </ul>
              ) : null}
            </section>
          )
        })}
      </div>

      {curseur ? (
        <p className="sv-rgs-suite">
          <button type="button" className="lc-btn" disabled={chargement} onClick={() => void charger(curseur)}>
            {chargement ? 'Lecture…' : 'Charger les découvertes suivantes'}
          </button>
        </p>
      ) : null}

      {peutGerer && selection.size > 0 && !validation ? createPortal(
        <div className="lc-bulkbar-zone">
          <div className="lc-bulkbar" role="toolbar" aria-label="Actions sur la sélection">
            <span className="lc-bulkbar__count">
              <span className="lc-dot" /> {selection.size} sélectionnée{selection.size > 1 ? 's' : ''}{selection.size >= LOT_MAX ? ` (${LOT_MAX} au plus)` : ''}
            </span>
            <span className="lc-bulkbar__actions">
              <button type="button" className="lc-btn lc-btn--primary" disabled={selectionValidable.length === 0 || enCours !== null}
                onClick={() => ouvrirValidation(selectionnes)}>Valider la sélection</button>
              <button type="button" className="lc-btn" disabled={enCours !== null} onClick={() => void ignorer(selectionnes)}>Ignorer</button>
            </span>
            <button type="button" className="lc-btn lc-btn--ghost lc-bulkbar__clear" onClick={() => setSelection(new Set())} aria-label="Vider la sélection">
              <X size={15} aria-hidden="true" />
            </button>
          </div>
        </div>,
        document.body,
      ) : null}

      {validation ? (
        <ModaleValidation lignes={validation} decouvertes={items} salles={salles}
          onFermer={() => setValidation(null)}
          onFait={(ids, texte) => {
            retirer(ids)
            setRetour({ ton: 'ok', texte })
            props.onCompte()
          }} />
      ) : null}
    </section>
  )
}

function LigneDecouverte(props: {
  d: Decouverte; par: 'type' | 'source'; salles: SalleRegistre[] | null; peutGerer: boolean; coche: boolean; ouvert: boolean; occupe: boolean
  onCocher: (v: boolean) => void; onOuvrir: () => void; onValider: () => void; onRattacher: () => void; onIgnorer: () => void
  onRemettre: (() => void) | null
}) {
  const { d } = props
  const prive = raisonViePrivee(d.vlan)
  const h = d.attach_hint
  const nom = d.suggested.name ?? d.hostname ?? d.value
  const preuves = preuvesLisibles(d.evidence)
  const age = (Date.now() - new Date(d.last_seen_at).getTime()) / 1000
  return (
    <li className={`sv-rgs-ligne${props.coche ? ' sv-rgs-ligne--coche' : ''}`}>
      <div className="sv-rgs-ligne__case">
        {props.peutGerer && peutValider(d) ? <Checkbox checked={props.coche} onChange={props.onCocher} ariaLabel={`Sélectionner ${nom}`} /> : null}
      </div>
      <div className="sv-rgs-ligne__corps">
        <div className="sv-rgs-ligne__titre">
          <span className="sv-rgs-nom">{nom}</span>
          {d.proposed_action ? <span className={`sv-rgs-geste sv-rgs-geste--${d.proposed_action}`}>{LIBELLE_GESTE[d.proposed_action]}</span> : null}
          {prive ? <span className="sv-rgs-geste sv-rgs-geste--prive">Compté seulement</span> : null}
        </div>
        <p className="sv-rgs-meta">
          <span>{props.par === 'type' ? libelleSource(d.source) : libelleType(d.suggested.kind)}</span>
          <span>Salle suggérée : {d.suggested.zone_code ? libelleSalle(props.salles, d.suggested.zone_code) : '—'}</span>
          <span>Confiance : {d.confidence === null ? '—' : `${d.confidence} %`}</span>
          <span>Vue {fmtAge(age)}{d.seen_count > 1 ? ` · ${d.seen_count} fois` : ''}</span>
          {d.status !== 'pending' ? <span>{d.status === 'accepted' ? 'Validée' : 'Ignorée'} par {acteur(d.decided_by)} le {fmtDateHeure(d.decided_at)}</span> : null}
        </p>
        {d.status === 'accepted' && d.device_uid ? (
          <p className="sv-rgs-meta"><Link href={`/infrastructure/appareils/${encodeURIComponent(d.device_uid)}`}>Poste « {d.device_uid} »</Link></p>
        ) : null}
        {prive ? <p className="sv-rgs-alerte">{prive}</p> : null}
        {d.local_mac ? <p className="sv-rgs-aide">Adresse MAC aléatoire (téléphone, ordinateur) : jamais rattachée automatiquement, elle peut changer.</p> : null}
        {h && d.status === 'pending' && !prive ? <p className={`sv-rgs-indice${h.replacement ? ' sv-rgs-indice--remplace' : ''}`}>{h.reason_fr}</p> : null}
        {props.ouvert ? (
          <dl className="sv-rgs-preuves">
            <div><dt>Identifiant</dt><dd className="sv-mono">{libelleSchema(d.scheme)} · {d.value}</dd></div>
            {d.vendor ? <div><dt>Fabricant</dt><dd>{d.vendor}</dd></div> : null}
            {d.ip ? <div><dt>IP</dt><dd className="sv-mono">{d.ip}</dd></div> : null}
            {d.vlan !== null ? <div><dt>VLAN</dt><dd>{d.vlan}</dd></div> : null}
            {d.suggested.uid ? <div><dt>Poste suggéré</dt><dd className="sv-mono">{d.suggested.uid}</dd></div> : null}
            <div><dt>Première vue</dt><dd>{fmtDateHeure(d.first_seen_at)}</dd></div>
            {preuves.map((p) => <div key={p.cle}><dt>{p.libelle}</dt><dd>{p.valeur}</dd></div>)}
          </dl>
        ) : null}
      </div>
      <div className="sv-rgs-ligne__actions">
        <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm" aria-expanded={props.ouvert} onClick={props.onOuvrir}>
          {props.ouvert ? 'Masquer les preuves' : 'Preuves'}
        </button>
        {props.peutGerer && rattachementPossible(d) ? (
          <button type="button" className="lc-btn lc-btn--sm" disabled={props.occupe} onClick={props.onRattacher}>
            <Link2 size={14} aria-hidden="true" /> {h?.replacement ? 'Exemplaire remplacé' : 'Rattacher'}
          </button>
        ) : null}
        {props.peutGerer && peutValider(d) ? (
          <button type="button" className="lc-btn lc-btn--sm" disabled={props.occupe} onClick={props.onValider}>Valider</button>
        ) : null}
        {props.peutGerer ? (
          <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm" disabled={props.occupe} onClick={props.onIgnorer}>Ignorer</button>
        ) : null}
        {props.onRemettre ? (
          <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm" disabled={props.occupe} onClick={props.onRemettre}>
            <RotateCcw size={14} aria-hidden="true" /> Remettre dans la file
          </button>
        ) : null}
      </div>
    </li>
  )
}

/**
 * Valider un lot : chaque ligne garde ses suggestions, modifiables (nom,
 * identifiant du poste, salle, type). Sonde applique chaque décision dans sa
 * propre transaction : les refus restent affichés, ligne par ligne, avec leur
 * raison, et peuvent être corrigés puis renvoyés.
 */
function ModaleValidation(props: {
  lignes: LigneValidation[]; decouvertes: Decouverte[]; salles: SalleRegistre[] | null
  onFermer: () => void; onFait: (ids: string[], texte: string) => void
}) {
  const [lignes, setLignes] = useState(props.lignes)
  const [uidSaisi, setUidSaisi] = useState<Set<string>>(new Set())
  const [erreurs, setErreurs] = useState<Record<string, string>>({})
  const [envoi, setEnvoi] = useState(false)
  const [retour, setRetour] = useState<Retour>(null)
  const parId = useMemo(() => new Map(props.decouvertes.map((d) => [d.id, d])), [props.decouvertes])

  const opts = optionsSalles(props.salles, lignes.map((l) => l.salle))
  const optsType: ComboItem[] = TYPES_POSTE
    .map((k) => ({ value: k, label: libelleType(k), hint: LIBELLE_FAMILLE[FAMILLE_TYPE[k] ?? ''] ?? '', keywords: MOTS_TYPE[k] ?? '' }))
    .sort((a, b) => a.label.localeCompare(b.label, 'fr'))
  const optsUrgence = Object.entries(LIBELLE_CRITICITE).map(([value, label]) => ({ value, label }))
  const optsFamille = Object.entries(LIBELLE_FAMILLE).map(([value, label]) => ({ value, label }))
  const maj = (id: string, p: Partial<LigneValidation>) => setLignes((ls) => ls.map((l) => (l.id === id ? { ...l, ...p } : l)))
  const problemes = lignes.map((l) => [l.id, problemeLigne(l)] as const).filter(([, p]) => p !== null)
  const doublons = (() => {
    const vus = new Map<string, number>()
    for (const l of lignes) if (l.geste === 'accept') vus.set(l.uid, (vus.get(l.uid) ?? 0) + 1)
    return new Set([...vus.entries()].filter(([, n]) => n > 1).map(([u]) => u))
  })()
  const bloque = problemes.length > 0 ? `${problemes.length} ligne${problemes.length > 1 ? 's' : ''} à compléter.`
    : doublons.size > 0 ? 'Deux lignes portent le même identifiant de poste.' : null

  const envoyer = async () => {
    if (bloque || lignes.length === 0) return
    setEnvoi(true); setRetour(null)
    const e = await ecrireSonde('POST', 'discovery', { decisions: decisionsValidation(lignes) }, { rien: 'aucune découverte n’a été validée' })
    setEnvoi(false)
    if (!e.ok || !estResultatDecisions(e.data)) { setRetour({ ton: 'ko', texte: e.ok ? 'Réponse inattendue de Sonde : relisez la file avant de recommencer.' : e.message }); return }
    const okIds = e.data.results.filter((r) => r.ok).map((r) => r.id)
    const refus: Record<string, string> = {}
    for (const r of e.data.results) if (!r.ok) refus[r.id] = r.code === 'COUNT_ONLY_VLAN' ? raisonViePrivee(parId.get(r.id)?.vlan ?? null) ?? r.message_fr : r.message_fr
    const texte = `${e.data.applied} découverte${e.data.applied > 1 ? 's' : ''} validée${e.data.applied > 1 ? 's' : ''} : ${e.data.applied > 1 ? 'les postes entrent' : 'le poste entre'} au registre et dans ${e.data.applied > 1 ? 'leur' : 'sa'} salle.`
    if (okIds.length > 0) props.onFait(okIds, e.data.failed > 0 ? `${texte} ${e.data.failed} refusée${e.data.failed > 1 ? 's' : ''}, restée${e.data.failed > 1 ? 's' : ''} dans la file.` : texte)
    if (e.data.failed === 0) { props.onFermer(); return }
    setLignes((ls) => ls.filter((l) => !okIds.includes(l.id)))
    setErreurs(refus)
    setRetour({ ton: 'ko', texte: `${e.data.failed} ligne${e.data.failed > 1 ? 's' : ''} refusée${e.data.failed > 1 ? 's' : ''} par Sonde : corrigez-les ci-dessous puis validez de nouveau.` })
  }

  return (
    <Modal open onClose={() => { if (!envoi) props.onFermer() }} ariaLabel="Valider les découvertes" width={980} owning>
      <div className="lc-modal__head">
        <div>
          <h2 className="lc-modal__title">Valider {lignes.length} découverte{lignes.length > 1 ? 's' : ''}</h2>
          <p className="sv-rgs-aide">Chaque poste validé entre au registre, s’affiche dans sa salle et est surveillé par Sonde. Le nom et la salle se corrigent ici ; l’identifiant du poste ne changera plus.</p>
        </div>
      </div>
      <div className="lc-modal__body sv-rgs-modale">
        {lignes.length > 1 ? (
          <div className="sv-rgs-modale__tous">
            <Field label="Même salle pour toutes les lignes" helper="Facultatif : chaque ligne reste modifiable ensuite.">
              <Select value="__choisir" placeholder="Choisir une salle…" options={opts} ariaLabel="Salle pour toutes les lignes"
                onChange={(v) => setLignes((ls) => ls.map((l) => (l.geste === 'accept' ? { ...l, salle: v || null } : l)))} />
            </Field>
            <Field label="Même urgence pour toutes les lignes" helper="Confort et Information ne font pas baisser l’état général.">
              <Select value="__choisir" placeholder="Choisir une urgence…" options={optsUrgence} ariaLabel="Urgence pour toutes les lignes"
                onChange={(v) => setLignes((ls) => ls.map((l) => (l.geste === 'accept' ? { ...l, urgence: v } : l)))} />
            </Field>
          </div>
        ) : null}
        <ol className="sv-rgs-lot">
          {lignes.map((l) => {
            const d = parId.get(l.id)
            const p = problemeLigne(l) ?? (l.geste === 'accept' && doublons.has(l.uid) ? 'Identifiant déjà pris par une autre ligne du lot.' : null)
            const refus = erreurs[l.id]
            return (
              <li key={l.id} className={`sv-rgs-lot__ligne${refus ? ' sv-rgs-lot__ligne--refus' : ''}`}>
                <p className="sv-rgs-lot__origine">
                  {libelleType(d?.suggested.kind ?? l.type)} · {libelleSource(d?.source)}{d?.confidence !== null && d?.confidence !== undefined ? ` · confiance ${d.confidence} %` : ''}
                </p>
                {l.geste === 'attach' ? (
                  <div className="sv-rgs-lot__rattache">
                    <p>Rattacher à <strong>« {l.cible} »</strong>{d?.attach_hint ? ` (${d.attach_hint.display_name})` : ''}.</p>
                    <Checkbox checked={l.remplacer} onChange={(v) => maj(l.id, { remplacer: v })}
                      label="Exemplaire remplacé : clore l’ancien identifiant du même genre (l’historique suit le poste)" />
                    <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm"
                      onClick={() => maj(l.id, { geste: 'accept', cible: null, remplacer: false, uid: uidDepuisNom(l.nom, l.type ?? 'poste') })}>
                      Créer plutôt un nouveau poste
                    </button>
                  </div>
                ) : (
                  <div className="sv-rgs-lot__champs">
                    <Field label="Nom affiché">
                      <TextInput value={l.nom} maxLength={200} ariaLabel="Nom affiché"
                        onChange={(v) => maj(l.id, uidSaisi.has(l.id) ? { nom: v } : { nom: v, uid: uidDepuisNom(v, l.type ?? 'poste') })} />
                    </Field>
                    <Field label="Identifiant du poste" helper="Immuable une fois créé.">
                      <TextInput value={l.uid} maxLength={63} ariaLabel="Identifiant du poste"
                        onChange={(v) => { setUidSaisi((s) => new Set(s).add(l.id)); maj(l.id, { uid: v.toLowerCase() }) }} />
                    </Field>
                    <Field label="Salle">
                      <Select value={l.salle ?? ''} options={opts} ariaLabel="Salle" onChange={(v) => maj(l.id, { salle: v || null })} />
                    </Field>
                    <Field label="Type">
                      <Combobox value={l.type} items={optsType} placeholder="Type à choisir" ariaLabel="Type du poste"
                        onChange={(v) => { if (v) maj(l.id, { type: v, urgence: urgenceParDefaut(v) }) }} />
                    </Field>
                    <Field label="Urgence" helper="Délai d’alerte si le poste tombe.">
                      <Select value={l.urgence} options={optsUrgence} ariaLabel="Urgence" onChange={(v) => maj(l.id, { urgence: v })} />
                    </Field>
                    {l.type === 'other' ? (
                      <Field label="Famille">
                        <Select value={l.famille ?? ''} placeholder="Famille à choisir" options={optsFamille} ariaLabel="Famille du poste" onChange={(v) => maj(l.id, { famille: v })} />
                      </Field>
                    ) : null}
                  </div>
                )}
                {p ? <p className="sv-rgs-lot__probleme" role="alert">{p}</p> : null}
                {refus ? <p className="sv-rgs-lot__probleme" role="alert">{refus}</p> : null}
              </li>
            )
          })}
        </ol>
        <LigneRetour retour={retour} />
        <div className="sv-rgs-pied">
          <button type="button" className="lc-btn lc-btn--ghost" disabled={envoi} onClick={props.onFermer}>Annuler</button>
          <button type="button" className="lc-btn lc-btn--primary" disabled={envoi || !!bloque || lignes.length === 0} title={bloque ?? undefined}
            onClick={() => void envoyer()}>
            {envoi ? 'Validation…' : `Valider ${lignes.length} découverte${lignes.length > 1 ? 's' : ''}`}
          </button>
        </div>
        {bloque ? <p className="sv-rgs-aide sv-rgs-pied__pourquoi">{bloque}</p> : null}
      </div>
    </Modal>
  )
}
